/**
 * Slicing a leather part in two (#1001, epic #781).
 *
 * This is NOT construction's slice. There, a cut splits a line into two lines
 * and an open result is fine. A part is a closed REGION, so each side of the
 * cut has to be sealed along the cut path before it can be a part at all —
 * otherwise you get two open arcs that render as nothing.
 *
 * The construction of each half:
 *
 *   1. Find where the stroke crosses the outline. Fewer than two crossings and
 *      there is no cut to make.
 *   2. Take the FIRST and LAST crossing along the stroke. A freehand drag
 *      wobbles and can cross a corner several times; spanning the outermost
 *      pair is what the user drew, and it degrades gracefully instead of
 *      refusing a slightly untidy stroke.
 *   3. Split the outline at those two points, giving two arcs.
 *   4. Seal ONE arc with the stroke segment between the crossings, and clip the
 *      result back to the source outline.
 *   5. Take the other half as the COMPLEMENT of the first.
 *
 * The seam between the halves is the stroke itself, so they tile exactly — no
 * kerf. (Subtracting a thin knife-shaped region would have been fewer lines but
 * would lose a sliver of leather and leave the two halves not quite meeting.)
 */
import type paper from "paper";
import type { Decoration, Piece, Vec2 } from "./Project";
import { pieceHardware, pieceStitchLines } from "./Project";
import { clonePieceForDuplicate } from "./pieceDuplicate";
import { seamPathData } from "./seamGeometry";

export interface SliceResult {
  /** Both halves, as closed pathData. Always exactly two. */
  halves: [string, string];
}

/** Below this area (mm²) a half is a sliver from a stroke that grazed the
 *  outline rather than crossing it — not a part anyone meant to make. */
const MIN_HALF_AREA_MM2 = 1;

/**
 * Cut a closed outline with a freehand stroke.
 *
 * Returns null when the stroke doesn't genuinely divide the shape: fewer than
 * two crossings, a degenerate stroke, or a result where one side is a sliver.
 * Callers treat null as "no cut", never as an error to report — a drag that
 * misses is an ordinary thing to do.
 */
export function slicePathWithStroke(
  shapePathData: string,
  stroke: ReadonlyArray<{ x: number; y: number }>,
  scope: paper.PaperScope,
): SliceResult | null {
  if (!shapePathData || stroke.length < 2) return null;

  let shape: paper.Path | null = null;
  let cut: paper.Path | null = null;
  const cleanup = () => {
    shape?.remove();
    cut?.remove();
  };
  try {
    const parsed = new scope.CompoundPath({
      pathData: shapePathData,
      insert: false,
    });
    const subs =
      parsed.children && parsed.children.length > 0
        ? (parsed.children as paper.Path[])
        : [parsed as unknown as paper.Path];
    // One region only. A compound outline (a part with an island) has no single
    // "two halves" answer, so it's left alone rather than guessed at.
    if (subs.length !== 1) {
      parsed.remove();
      return null;
    }
    shape = subs[0]!.clone({ insert: false }) as paper.Path;
    parsed.remove();
    if (!shape.closed) {
      cleanup();
      return null;
    }

    cut = new scope.Path({ insert: false });
    for (const p of stroke) cut.add(new scope.Point(p.x, p.y));
    if (cut.segments.length < 2) {
      cleanup();
      return null;
    }

    const hits = shape.getIntersections(cut);
    if (hits.length < 2) {
      cleanup();
      return null;
    }

    // Order by position along the STROKE, so "first" and "last" mean what the
    // user drew rather than where they happen to sit on the outline.
    const along = hits
      .map((h) => ({ loc: h, cutOffset: h.intersection?.offset ?? 0 }))
      .sort((a, b) => a.cutOffset - b.cutOffset);
    const entry = along[0]!;
    const exit = along[along.length - 1]!;
    if (Math.abs(exit.cutOffset - entry.cutOffset) < 1e-6) {
      cleanup();
      return null;
    }

    // The stroke between the two crossings — the seam both halves share.
    const seam = cut.clone({ insert: false }) as paper.Path;
    const tail = seam.splitAt(seam.getLocationAt(entry.cutOffset));
    // `splitAt` returns the far side; the near side is discarded with `seam`.
    const middle = tail ?? seam;
    if (tail) seam.remove();
    const afterExit = middle.splitAt(
      middle.getLocationAt(exit.cutOffset - entry.cutOffset),
    );
    afterExit?.remove();
    if (middle.segments.length < 2) {
      middle.remove();
      cleanup();
      return null;
    }

    // Split the outline at both crossings, giving two arcs.
    const ring = shape.clone({ insert: false }) as paper.Path;
    const p1 = ring.getNearestLocation(entry.loc.point);
    ring.splitAt(p1);
    const arcB = ring.splitAt(ring.getNearestLocation(exit.loc.point));
    if (!arcB) {
      ring.remove();
      middle.remove();
      cleanup();
      return null;
    }

    // One half is built by sealing an arc with the seam and clipping it back to
    // the source. The OTHER is the complement — everything the first half left
    // behind.
    //
    // Sealing the second arc the same way would be the symmetrical thing to do,
    // and it's wrong. A stroke is free to leave the part and re-enter it between
    // its first and last crossing (wobble across a corner is enough), and the
    // lobe that carves off then belongs to neither sealed arc: both halves would
    // come out valid, and a bite of leather would have quietly vanished between
    // them. Complementing can't lose anything — whatever isn't in the first half
    // is, by construction, in the second.
    const first = sealArc(ring, middle, shape);
    const second = first ? complementOf(shape, first.d, scope) : null;

    ring.remove();
    arcB.remove();
    middle.remove();
    cleanup();

    if (!first || !second) return null;
    const halves = [first, second];
    if (halves.some((h) => !h.d || h.area < MIN_HALF_AREA_MM2)) return null;
    return { halves: [halves[0]!.d, halves[1]!.d] };
  } catch {
    cleanup();
    return null;
  }
}

/** Seal one arc of the split outline with the seam, then trim the result back
 *  to the source. Returns null if the two won't join into a closed region. */
function sealArc(
  arc: paper.Path,
  seam: paper.Path,
  source: paper.Path,
): { d: string; area: number } | null {
  const sealed = arc.clone({ insert: false }) as paper.Path;
  const bridge = seam.clone({ insert: false }) as paper.Path;
  // Join needs the endpoints to meet; the seam runs one way, so flip it for
  // whichever arc it doesn't already line up with.
  if (
    sealed.lastSegment.point.getDistance(bridge.firstSegment.point) >
    sealed.lastSegment.point.getDistance(bridge.lastSegment.point)
  ) {
    bridge.reverse();
  }
  sealed.join(bridge, 1e-6);
  sealed.closed = true;
  const out = sealed.segments.length >= 3 ? clipToSource(sealed, source) : null;
  sealed.remove();
  bridge.remove();
  return out;
}

/** Everything in `source` that the first half didn't take. */
function complementOf(
  source: paper.Path,
  firstHalfPathData: string,
  scope: paper.PaperScope,
): { d: string; area: number } | null {
  let first: paper.CompoundPath | null = null;
  try {
    first = new scope.CompoundPath({
      pathData: firstHalfPathData,
      insert: false,
    });
  } catch {
    return null;
  }
  let rest: paper.PathItem | null = null;
  try {
    rest = source.subtract(first, { insert: false });
  } catch {
    first.remove();
    return null;
  }
  first.remove();
  if (!rest) return null;
  const out = largestRegion(rest);
  rest.remove();
  return out;
}

/** The biggest region of a possibly-compound result, as pathData + area. A part
 *  is ONE region — the same rule `slicePathWithStroke` applies to its input — so
 *  islands a wandering stroke shook loose are dropped rather than merged into a
 *  part made of disconnected scraps. */
function largestRegion(
  item: paper.PathItem,
): { d: string; area: number } | null {
  const regions =
    item.children && item.children.length > 0
      ? (item.children as paper.Path[])
      : [item as paper.Path];
  let best: paper.Path | null = null;
  let bestArea = -1;
  for (const r of regions) {
    const a = Math.abs(r.area);
    if (a > bestArea) {
      bestArea = a;
      best = r;
    }
  }
  if (!best) return null;
  removeSpurs(best);
  return best.pathData ? { d: best.pathData, area: Math.abs(best.area) } : null;
}

/**
 * Strip zero-area spurs from a boolean result.
 *
 * Where a cut runs along an edge the two halves share, paper's boolean ops can
 * leave the outline walking out along that edge and straight back — a spur that
 * encloses nothing. The AREA is right either way, which is what makes this easy
 * to miss: what's wrong is the BOUNDS (a half reports itself as spanning the
 * whole part) and the anchors, which put an edit point on a fold in the outline.
 *
 * Only straight reversals are removed, so a genuine cusp between two curves
 * survives.
 */
function removeSpurs(path: paper.Path): void {
  // A micron. Boolean residue lands a few 1e-6 mm off "the same point" — NOT
  // exactly zero, which is the trap: a tolerance tight enough to look rigorous
  // (1e-6) misses it, and the spur survives while serialising to a `l0,0` that
  // reads as if it had been caught. Nothing below 1e-5 mm survives pathData's
  // 5-decimal round-trip anyway, so there is no real geometry down here to lose.
  const COINCIDENT_MM = 1e-4;
  for (let guard = 0; guard < 64 && path.segments.length > 3; guard++) {
    let removed = false;
    for (const seg of [...path.segments]) {
      const prev = seg.previous;
      const next = seg.next;
      if (!prev || !next) continue;
      const inDir = seg.point.subtract(prev.point);
      const outDir = next.point.subtract(seg.point);
      const doubled =
        inDir.length < COINCIDENT_MM || outDir.length < COINCIDENT_MM;
      const reverses =
        !doubled &&
        seg.handleIn.isZero() &&
        seg.handleOut.isZero() &&
        inDir.normalize().dot(outDir.normalize()) < -1 + 1e-6;
      if (!doubled && !reverses) continue;
      seg.remove();
      removed = true;
      break;
    }
    if (!removed) return;
  }
}

/**
 * Trim a sealed half back to the outline it came from.
 *
 * A freehand stroke doesn't have to stay inside the part between its first and
 * last crossing — wobble across a corner, or a deliberate detour, and it leaves
 * and re-enters. Sealing along that excursion would hand the half leather the
 * part never had, so it's intersected with the source.
 */
function clipToSource(
  half: paper.Path,
  source: paper.Path,
): { d: string; area: number } {
  const asIs = { d: half.pathData, area: Math.abs(half.area) };
  let clipped: paper.PathItem | null = null;
  try {
    clipped = half.intersect(source, { insert: false });
  } catch {
    return asIs;
  }
  if (!clipped) return asIs;
  const out = largestRegion(clipped);
  clipped.remove();
  return out ?? asIs;
}

/**
 * Which half a child feature belongs to, by testing a representative point.
 * Returns the index into `halves`, or null when the point falls in neither
 * (a feature straddling the cut).
 */
export function halfContaining(
  halves: readonly [string, string],
  pt: { x: number; y: number },
  scope: paper.PaperScope,
): 0 | 1 | null {
  for (let i = 0; i < 2; i++) {
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({ pathData: halves[i]!, insert: false });
    } catch {
      continue;
    }
    const inside = cp.contains(new scope.Point(pt.x, pt.y));
    cp.remove();
    if (inside) return i === 0 ? 0 : 1;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Splitting a whole part — the outline plus everything living on it
// ---------------------------------------------------------------------------

/** The geometry a child feature occupies, for deciding which half it lands in.
 *  A `point` feature (a hole, a stamp) is small enough that where its anchor
 *  sits IS the answer; a `path` feature is extended, so it has to be tested as
 *  a whole. Features with neither travel with the first half rather than being
 *  quietly destroyed — nothing about them says where they belong. */
type ChildGeometry =
  | { kind: "point"; at: Vec2 }
  | { kind: "path"; d: string }
  | { kind: "none" };

/** Where a decoration lives. The kinds differ in which field carries their
 *  geometry, and an `offset` decoration is derived from a source path that may
 *  itself be the part outline — so it's tested on that source. */
function decorationGeometry(dec: Decoration): ChildGeometry {
  switch (dec.kind) {
    case "stamp":
      return { kind: "point", at: dec.position };
    case "lineStamp":
    case "freePath":
      return { kind: "path", d: dec.pathData };
    case "fill":
      return { kind: "path", d: dec.regionPathData };
    case "offset":
      return { kind: "path", d: dec.sourcePathData };
  }
}

/**
 * Decide which half a child feature belongs to.
 *
 * "Wholly inside" is tested by intersection with the half's boundary rather
 * than by sampling points: a child that crosses the cut necessarily crosses the
 * boundary, and this reads the same for an open seam as for a closed cutout —
 * a containment test can't say anything useful about an arc.
 *
 * Returns the half index, or null for a feature that straddles the cut.
 */
function childHalf(
  halfPaths: readonly [paper.CompoundPath, paper.CompoundPath],
  geom: ChildGeometry,
  scope: paper.PaperScope,
): 0 | 1 | null {
  if (geom.kind === "none") return 0;
  if (geom.kind === "point") {
    const p = new scope.Point(geom.at.x, geom.at.y);
    if (halfPaths[0].contains(p)) return 0;
    if (halfPaths[1].contains(p)) return 1;
    return null;
  }
  let child: paper.CompoundPath | null = null;
  try {
    child = new scope.CompoundPath({ pathData: geom.d, insert: false });
  } catch {
    return null;
  }
  const first = child.firstSegment?.point ?? null;
  let answer: 0 | 1 | null = null;
  for (let i = 0; i < 2 && answer === null; i++) {
    const half = halfPaths[i]!;
    let crosses = true;
    try {
      crosses = half.getIntersections(child).length > 0;
    } catch {
      crosses = true;
    }
    if (crosses) continue;
    if (first && half.contains(first)) answer = i === 0 ? 0 : 1;
  }
  child.remove();
  return answer;
}

/**
 * Cut a part in two.
 *
 * Both halves come out as independent parts with fresh ids throughout — the
 * source is replaced, not edited — carrying over everything that describes the
 * leather (material, thickness, colour, rotation frame) and taking the names
 * they're given.
 *
 * Three pieces of metadata are deliberately DROPPED:
 *   - `cornerRounding` (via {@link Piece.cornerRounding}) — its `sharpPathData`
 *     is the whole un-cut outline, and rounding re-derives from that on every
 *     commit, so keeping it would grow the half back into the original part.
 *   - `smoothAnchors` / `bezierMemory` — both address anchors by index, and the
 *     cut renumbers them.
 *   - `constructionOutline`, dropped by `clonePieceForDuplicate`: a linked half
 *     would re-derive from the same scaffold and undo the cut.
 *
 * CHILDREN follow their geometry. A hole, stamp, cutout or seam that lands
 * wholly inside one half goes with that half. One STRADDLING the cut is dropped
 * — the alternative is a feature hanging off the edge of a part it no longer
 * fits, and clipping a seam would leave a stitch row ending in mid-air. Undo
 * takes it back if that wasn't wanted.
 *
 * Returns null when the stroke doesn't divide the outline (see
 * {@link slicePathWithStroke}).
 */
export function splitPieceWithStroke(
  piece: Piece,
  stroke: ReadonlyArray<{ x: number; y: number }>,
  scope: paper.PaperScope,
  names: readonly [string, string],
): [Piece, Piece] | null {
  const cut = slicePathWithStroke(piece.shapePathData, stroke, scope);
  if (!cut) return null;

  let halfPaths: [paper.CompoundPath, paper.CompoundPath];
  try {
    halfPaths = [
      new scope.CompoundPath({ pathData: cut.halves[0], insert: false }),
      new scope.CompoundPath({ pathData: cut.halves[1], insert: false }),
    ];
  } catch {
    return null;
  }

  const out: Piece[] = [];
  for (let i = 0; i < 2; i++) {
    const half = i as 0 | 1;
    // Clone first, so every child on this half gets its own fresh id and the
    // two halves share nothing mutable.
    const clone = clonePieceForDuplicate(piece, names[half]!);
    const keptHoleIds = new Set<string>();
    const holes = clone.holes.filter((h) => {
      const keep =
        childHalf(halfPaths, { kind: "point", at: h.position }, scope) === half;
      if (keep) keptHoleIds.add(h.id);
      return keep;
    });
    const next: Piece = {
      ...clone,
      shapePathData: cut.halves[half],
      holes,
      cutouts: clone.cutouts.filter(
        (c) =>
          childHalf(halfPaths, { kind: "path", d: c.pathData }, scope) === half,
      ),
      stitchLines: pieceStitchLines(clone).filter((s) => {
        const d = seamPathData(s);
        if (!d) return false;
        return childHalf(halfPaths, { kind: "path", d }, scope) === half;
      }),
      decorations: clone.decorations.filter(
        (d) => childHalf(halfPaths, decorationGeometry(d), scope) === half,
      ),
      // Hardware is anchored to a hole on the same part, so it simply follows
      // the hole rather than being placed independently.
      hardware: pieceHardware(clone).filter((hw) => keptHoleIds.has(hw.holeId)),
    };
    delete next.cornerRounding;
    delete next.smoothAnchors;
    delete next.bezierMemory;
    out.push(next);
  }

  halfPaths[0].remove();
  halfPaths[1].remove();
  return [out[0]!, out[1]!];
}
